import { useCallback, useEffect, useState } from "react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { InsightsMonthlyRevenuePoint } from "@shared-core/data/api-types";
import { MonthlyRevenueChart } from "./MonthlyRevenueChart";

// The Monthly tab: loads the composed /views/insights/monthly-revenue series
// and renders the booked/invoiced revenue+contribution chart. This is THE
// template the other four Insights tabs copy — fetch a composed view, render
// a recharts component, cover loading/empty/error.

type State =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; months: InsightsMonthlyRevenuePoint[] };

// `tradingCompany` is a code chosen in the page header's Trading company
// control; absent is the group. Changing it refetches the series.
export function MonthlyRevenueTab({
  tradingCompany,
}: {
  tradingCompany?: string;
}) {
  const adapter = useDataAdapter();
  const [state, setState] = useState<State>({ status: "loading" });

  const load = useCallback(async () => {
    setState({ status: "loading" });
    try {
      const result = await adapter.getInsightsMonthlyRevenue(
        undefined,
        tradingCompany,
      );
      setState({ status: "ready", months: result.months });
    } catch {
      setState({ status: "error" });
    }
  }, [adapter, tradingCompany]);

  useEffect(() => {
    load();
  }, [load]);

  if (state.status === "loading") {
    return (
      <div className="border-border bg-surface text-muted-foreground flex min-h-[320px] items-center justify-center rounded-lg border text-sm">
        Loading revenue…
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="border-border bg-surface flex min-h-[320px] flex-col items-center justify-center gap-3 rounded-lg border text-center">
        <p className="text-destructive text-sm">
          Couldn’t load revenue data. Please try again.
        </p>
        <button
          type="button"
          onClick={load}
          className="text-primary text-sm underline hover:no-underline"
        >
          Retry
        </button>
      </div>
    );
  }

  return <MonthlyRevenueChart months={state.months} />;
}
